import React, { useState, useRef, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Navbar } from '../components/Navbar';
import { CanvasWorkspace } from '../components/studio/CanvasWorkspace';
import { PhotoEditor } from '../components/studio/PhotoEditor';
import { TextOverlay } from '../components/studio/TextOverlay';
import { TemplateGallery } from '../components/studio/TemplateGallery';
import { CollageBuilder, LAYOUTS, GAP } from '../components/studio/CollageBuilder';
import { CarouselBuilder } from '../components/studio/CarouselBuilder';
import { ImageLibrary } from '../components/ImageLibrary';
import { Button } from '../components/ui/button';
import { useToast } from '../hooks/use-toast';
import { useClient } from '../context/ClientContext';
import { 
  ImagePlus, Type, LayoutTemplate, Grid2x2, Layers, 
  Download, Save, ArrowLeft, RotateCcw, Trash2, Upload, Image, Crop
} from 'lucide-react';
import { ImageCropTool } from '../components/ImageCropTool';

const TOOLS = [
  { id: 'edit', label: 'Edit', icon: ImagePlus },
  { id: 'text', label: 'Text', icon: Type },
  { id: 'templates', label: 'Templates', icon: LayoutTemplate },
  { id: 'collage', label: 'Collage', icon: Grid2x2 },
  { id: 'carousel', label: 'Carousel', icon: Layers },
];

const DEFAULT_FILTERS = { brightness: 100, contrast: 100, saturate: 100, grayscale: 0, sepia: 0, blur: 0 };

export function CreativeStudioPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { toast } = useToast();
  const { activeClient } = useClient();
  const stageRef = useRef(null);
  const fileInputRef = useRef(null);

  // Core state
  const [activeTool, setActiveTool] = useState('edit');
  const [backgroundImage, setBackgroundImage] = useState(null);
  const [backgroundColor, setBackgroundColor] = useState('#ffffff');
  const [canvasWidth, setCanvasWidth] = useState(1080);
  const [canvasHeight, setCanvasHeight] = useState(1080);
  const [elements, setElements] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [filters, setFilters] = useState({ ...DEFAULT_FILTERS });
  const [rotation, setRotation] = useState(0);

  // Collage state
  const [collageLayout, setCollageLayout] = useState('2-side');
  const [collageImages, setCollageImages] = useState({});

  // Carousel state
  const [carouselSlides, setCarouselSlides] = useState([
    { imageUrl: null, caption: '', backgroundColor: '#ffffff', elements: [] }
  ]);
  const [activeSlide, setActiveSlide] = useState(0);
  const [showLibrary, setShowLibrary] = useState(false);
  const [libraryTarget, setLibraryTarget] = useState('background');
  const [showCropTool, setShowCropTool] = useState(false);
  const [cropImageSrc, setCropImageSrc] = useState(null);
  const [cropTarget, setCropTarget] = useState(null); // 'background' or element id
  const [isSavingToPost, setIsSavingToPost] = useState(false);

  // Post context from query params
  const postId = searchParams.get('postId');
  const returnTo = searchParams.get('returnTo');
  const toolParam = searchParams.get('tool');
  const canvasParam = searchParams.get('canvas');

  // Auto-select tool and canvas based on URL params (from PostModal smart link)
  React.useEffect(() => {
    if (toolParam && ['edit', 'text', 'templates', 'collage', 'carousel'].includes(toolParam)) {
      setActiveTool(toolParam);
    }
    if (canvasParam === 'story') {
      setCanvasWidth(1080);
      setCanvasHeight(1920);
    }
  }, [toolParam, canvasParam]);

  // Load post's existing image as background when coming from PostModal
  React.useEffect(() => {
    if (postId) {
      const loadPostImage = async () => {
        try {
          const { postsApi } = await import('../services/api');
          const post = await postsApi.getOne(postId);
          if (post?.image_url) {
            const API = process.env.REACT_APP_BACKEND_URL;
            const imgUrl = post.image_url.startsWith('http') ? post.image_url : `${API}${post.image_url}`;
            // Verify image exists before setting
            const testRes = await fetch(imgUrl, { method: 'HEAD' });
            if (testRes.ok) {
              setBackgroundImage(imgUrl);
            }
          }
        } catch (e) {
          // Post might not have an image, that's fine
        }
      };
      loadPostImage();
    }
  }, [postId]); // 'background' | 'element' | 'collage-0' etc.

  // Scale canvas to fit viewport (must consider both width AND height for vertical formats like Stories)
  const maxDisplayWidth = typeof window !== 'undefined' ? Math.min(640, window.innerWidth - 380) : 640;
  const maxDisplayHeight = typeof window !== 'undefined' ? window.innerHeight - 180 : 600;
  const scale = Math.min(maxDisplayWidth / canvasWidth, maxDisplayHeight / canvasHeight, 0.6);

  // === Image Upload ===
  const handleImageUpload = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const url = URL.createObjectURL(file);
    
    if (activeTool === 'edit' || activeTool === 'text' || activeTool === 'templates') {
      setBackgroundImage(url);
    }
    e.target.value = '';
  };

  // === Crop Tool ===
  const openCropTool = (src, target) => {
    setCropImageSrc(src);
    setCropTarget(target);
    setShowCropTool(true);
  };

  const handleCropComplete = ({ file, previewUrl }) => {
    if (cropTarget === 'background') {
      setBackgroundImage(previewUrl);
    } else if (cropTarget) {
      // Replace the image element's src with cropped version
      updateElement(cropTarget, { src: previewUrl });
    }
    setCropTarget(null);
    setCropImageSrc(null);
  };

  // === Library Image Selection ===
  const openLibrary = (target = 'background') => {
    setLibraryTarget(target);
    setShowLibrary(true);
  };

  const handleLibrarySelect = (image) => {
    const imageUrl = image.url;
    
    if (libraryTarget === 'background') {
      setBackgroundImage(imageUrl);
      toast({ title: 'Image loaded', description: image.name });
    } else if (libraryTarget === 'element') {
      addElement({
        id: `img-${Date.now()}`,
        type: 'image',
        src: imageUrl,
        x: 100,
        y: 100,
        width: 400,
        height: 400,
      });
      toast({ title: 'Image added to canvas', description: image.name });
    } else if (libraryTarget.startsWith('collage-')) {
      const slotIndex = parseInt(libraryTarget.split('-')[1]);
      handleCollageAddImage(slotIndex, imageUrl, null);
      toast({ title: `Image added to slot ${slotIndex + 1}`, description: image.name });
    } else if (libraryTarget === 'carousel-slide') {
      handleUpdateSlide(activeSlide, { imageUrl });
      toast({ title: `Image set for slide ${activeSlide + 1}`, description: image.name });
    }
    setShowLibrary(false);
  };

  // === Element Management ===
  const addElement = useCallback((element) => {
    setElements(prev => [...prev, element]);
    setSelectedId(element.id);
  }, []);

  const updateElement = useCallback((id, changes) => {
    setElements(prev => prev.map(el => el.id === id ? { ...el, ...changes } : el));
  }, []);

  const deleteElement = useCallback((id) => {
    setElements(prev => prev.filter(el => el.id !== id));
    if (selectedId === id) setSelectedId(null);
  }, [selectedId]);

  // === Photo Editor Actions ===
  const handleRotate = (degrees) => {
    setRotation(prev => (prev + degrees) % 360);
  };

  const handleFlip = (direction) => {
    // Add flip via elements transform
    toast({ title: `Flipped ${direction === 'h' ? 'horizontally' : 'vertically'}` });
  };

  const handleReset = () => {
    setFilters({ ...DEFAULT_FILTERS });
    setRotation(0);
    toast({ title: 'Filters reset' });
  };

  // === Template Selection ===
  const handleSelectTemplate = (template) => {
    setCanvasWidth(template.canvas.width);
    setCanvasHeight(template.canvas.height);
    setBackgroundColor(template.canvas.backgroundColor);
    setBackgroundImage(null);
    setElements(template.canvas.elements.map(el => ({ ...el })));
    setSelectedId(null);
    setFilters({ ...DEFAULT_FILTERS });
    toast({ title: `Template: ${template.name}`, description: 'Click any element to edit it' });
  };

  // === Collage ===
  const handleCollageAddImage = (slotIndex, url, file) => {
    setCollageImages(prev => ({ ...prev, [slotIndex]: { url, file } }));
    // Build collage as canvas elements
    rebuildCollage(collageLayout, { ...collageImages, [slotIndex]: { url, file } });
  };

  const handleCollageRemoveImage = (slotIndex) => {
    const updated = { ...collageImages };
    delete updated[slotIndex];
    setCollageImages(updated);
    rebuildCollage(collageLayout, updated);
  };

  const handleCollageLayoutChange = (layoutId) => {
    setCollageLayout(layoutId);
    rebuildCollage(layoutId, collageImages);
  };

  const rebuildCollage = (layoutId, images) => {
    const layout = LAYOUTS.find(l => l.id === layoutId);
    if (!layout) return;
    
    const newElements = layout.slots.map((slot, i) => {
      if (images[i]) {
        return {
          id: `collage-img-${i}`,
          type: 'image',
          src: images[i].url,
          x: slot.x * canvasWidth + GAP,
          y: slot.y * canvasHeight + GAP,
          width: slot.w * canvasWidth - GAP * 2,
          height: slot.h * canvasHeight - GAP * 2,
        };
      }
      return {
        id: `collage-placeholder-${i}`,
        type: 'rect',
        x: slot.x * canvasWidth + GAP,
        y: slot.y * canvasHeight + GAP,
        width: slot.w * canvasWidth - GAP * 2,
        height: slot.h * canvasHeight - GAP * 2,
        fill: '#f1f5f9',
      };
    });
    setElements(newElements);
  };

  // === Carousel ===
  const handleAddSlide = () => {
    setCarouselSlides(prev => [...prev, { imageUrl: null, caption: '', backgroundColor: '#ffffff', elements: [] }]);
    setActiveSlide(carouselSlides.length);
  };

  const handleRemoveSlide = (index) => {
    if (carouselSlides.length <= 1) return;
    setCarouselSlides(prev => prev.filter((_, i) => i !== index));
    setActiveSlide(Math.min(activeSlide, carouselSlides.length - 2));
  };

  const handleUpdateSlide = (index, changes) => {
    setCarouselSlides(prev => prev.map((s, i) => i === index ? { ...s, ...changes } : s));
    // Update canvas to reflect active slide
    if (index === activeSlide) {
      if (changes.imageUrl !== undefined) setBackgroundImage(changes.imageUrl);
      if (changes.backgroundColor !== undefined) setBackgroundColor(changes.backgroundColor);
    }
  };

  const handleSetActiveSlide = (index) => {
    setActiveSlide(index);
    const slide = carouselSlides[index];
    if (slide) {
      setBackgroundImage(slide.imageUrl);
      setBackgroundColor(slide.backgroundColor || '#ffffff');
    }
  };

  const handleReorderSlide = (from, to) => {
    if (to < 0 || to >= carouselSlides.length) return;
    const updated = [...carouselSlides];
    const [moved] = updated.splice(from, 1);
    updated.splice(to, 0, moved);
    setCarouselSlides(updated);
    setActiveSlide(to);
  };

  // === Export ===
  const handleExport = () => {
    if (!stageRef.current) return;
    const stage = stageRef.current;
    
    // Deselect before export
    setSelectedId(null);
    
    setTimeout(() => {
      const uri = stage.toDataURL({ pixelRatio: 2 });
      const link = document.createElement('a');
      link.download = `creative-${Date.now()}.png`;
      link.href = uri;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      toast({ title: 'Image exported!', description: 'Check your downloads folder' });
    }, 100);
  };

  const handleExportAllSlides = () => {
    if (activeTool !== 'carousel') return;
    toast({ title: 'Exporting slides...', description: `${carouselSlides.length} slides` });
    
    carouselSlides.forEach((slide, i) => {
      handleSetActiveSlide(i);
      setTimeout(() => {
        if (stageRef.current) {
          const uri = stageRef.current.toDataURL({ pixelRatio: 2 });
          const link = document.createElement('a');
          link.download = `carousel-slide-${i + 1}.png`;
          link.href = uri;
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
        }
      }, 300 * (i + 1));
    });
  };

  // === Save to Post ===
  const handleSaveToPost = async () => {
    if (!stageRef.current || !postId) return;
    setIsSavingToPost(true);
    setSelectedId(null);

    try {
      // Wait for deselection to render
      await new Promise(r => setTimeout(r, 150));

      // Export canvas to blob
      const dataUrl = stageRef.current.toDataURL({ pixelRatio: 2 });
      const res = await fetch(dataUrl);
      const blob = await res.blob();

      // Upload as file
      const formData = new FormData();
      formData.append('file', blob, `studio-${postId}.png`);
      
      const API = process.env.REACT_APP_BACKEND_URL;
      const uploadRes = await fetch(`${API}/api/upload`, {
        method: 'POST',
        body: formData,
        credentials: 'include',
      });
      const uploadData = await uploadRes.json();

      // Update the post's image_url
      const { postsApi } = await import('../services/api');
      await postsApi.update(postId, { image_url: `/api/files/${uploadData.id}` });

      toast({ title: 'Saved to post!', description: 'Image attached successfully' });

      // Navigate back
      if (returnTo) {
        navigate(returnTo);
      } else {
        navigate('/dashboard');
      }
    } catch (error) {
      toast({ title: 'Failed to save', description: error.message, variant: 'destructive' });
    } finally {
      setIsSavingToPost(false);
    }
  };

  // Canvas sizes
  const CANVAS_SIZES = [
    { label: 'Square', w: 1080, h: 1080 },
    { label: 'Portrait', w: 1080, h: 1350 },
    { label: 'Story', w: 1080, h: 1920 },
    { label: 'Landscape', w: 1200, h: 628 },
  ];

  return (
    <div className="min-h-screen page-background" data-testid="creative-studio-page">
      <Navbar />

      <div className="flex h-[calc(100vh-64px)]">
        {/* Left Tool Bar */}
        <div className="w-14 bg-white border-r border-[#e2e8f0] flex flex-col items-center py-4 gap-1">
          {TOOLS.map((tool) => {
            const Icon = tool.icon;
            return (
              <button
                key={tool.id}
                onClick={() => setActiveTool(tool.id)}
                className={`w-10 h-10 rounded-xl flex items-center justify-center transition-colors ${
                  activeTool === tool.id
                    ? 'bg-[#2563eb] text-white'
                    : 'text-[#94a3b8] hover:text-[#475569] hover:bg-[#f1f5f9]'
                }`}
                title={tool.label}
                data-testid={`tool-${tool.id}`}
              >
                <Icon className="w-5 h-5" />
              </button>
            );
          })}
          
          <div className="mt-auto flex flex-col gap-1">
            <button
              onClick={() => navigate('/dashboard')}
              className="w-10 h-10 rounded-xl flex items-center justify-center text-[#94a3b8] hover:text-[#475569] hover:bg-[#f1f5f9]"
              title="Back to Dashboard"
              data-testid="back-to-dashboard-btn"
            >
              <ArrowLeft className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Left Panel - Tool Controls */}
        <div className="w-72 bg-white border-r border-[#e2e8f0] overflow-y-auto p-4">
          <h2 className="text-sm font-bold text-[#1e293b] mb-4" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
            {TOOLS.find(t => t.id === activeTool)?.label || 'Tools'}
          </h2>

          {/* Canvas Size */}
          {(activeTool === 'edit' || activeTool === 'text') && (
            <div className="mb-5">
              <label className="text-xs font-bold uppercase tracking-wider text-[#64748b] mb-2 block">Canvas Size</label>
              <div className="grid grid-cols-2 gap-1.5">
                {CANVAS_SIZES.map(s => (
                  <button
                    key={s.label}
                    onClick={() => { setCanvasWidth(s.w); setCanvasHeight(s.h); }}
                    className={`px-2 py-1.5 text-xs font-medium rounded-lg border transition-colors ${
                      canvasWidth === s.w && canvasHeight === s.h
                        ? 'border-[#2563eb] bg-[#eff6ff] text-[#2563eb]'
                        : 'border-[#e2e8f0] text-[#475569]'
                    }`}
                    data-testid={`size-${s.label.toLowerCase()}`}
                  >
                    {s.label}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Background Color */}
          {(activeTool === 'edit' || activeTool === 'text') && (
            <div className="mb-5">
              <label className="text-xs font-bold uppercase tracking-wider text-[#64748b] mb-2 block">Background Color</label>
              <div className="flex items-center gap-2 flex-wrap">
                {/* Quick presets: white, brand colors, black */}
                {[
                  { color: '#ffffff', label: 'White' },
                  { color: activeClient?.primary_color || '#4A5568', label: 'Brand' },
                  { color: activeClient?.accent_color || '#2D3748', label: 'Accent' },
                  { color: '#f1f5f9', label: 'Light' },
                  { color: '#1e293b', label: 'Dark' },
                  { color: '#000000', label: 'Black' },
                ].map(p => (
                  <button
                    key={p.label}
                    onClick={() => setBackgroundColor(p.color)}
                    className={`w-7 h-7 rounded-md border-2 transition-all ${
                      backgroundColor === p.color ? 'border-[#2563eb] scale-110 shadow-md' : 'border-gray-200 hover:border-gray-400'
                    }`}
                    style={{ backgroundColor: p.color }}
                    title={p.label}
                    data-testid={`bg-color-${p.label.toLowerCase()}`}
                  />
                ))}
                {/* Custom color picker */}
                <label className="relative w-7 h-7 rounded-md border-2 border-dashed border-gray-300 hover:border-gray-400 cursor-pointer flex items-center justify-center" title="Custom color">
                  <span className="text-[10px] text-gray-400">+</span>
                  <input
                    type="color"
                    value={backgroundColor}
                    onChange={(e) => setBackgroundColor(e.target.value)}
                    className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                    data-testid="bg-color-custom"
                  />
                </label>
              </div>
            </div>
          )}

          {/* Upload Image / Library */}
          {(activeTool === 'edit' || activeTool === 'text') && (
            <div className="mb-5 space-y-2">
              <input ref={fileInputRef} type="file" accept="image/*" onChange={handleImageUpload} className="hidden" />
              <Button
                variant="outline"
                onClick={() => openLibrary('background')}
                className="w-full border-[#e2e8f0] h-12 gap-2"
                data-testid="from-library-btn"
              >
                <Image className="w-4 h-4 text-[#2563eb]" />
                <span className="text-sm text-[#475569]">From Library</span>
              </Button>
              <div className="grid grid-cols-2 gap-2">
                <Button
                  variant="outline"
                  onClick={() => fileInputRef.current?.click()}
                  className="border-dashed border-[#e2e8f0] h-10 gap-1"
                  data-testid="upload-image-btn"
                >
                  <Upload className="w-3.5 h-3.5 text-[#94a3b8]" />
                  <span className="text-xs text-[#94a3b8]">Upload</span>
                </Button>
                <Button
                  variant="outline"
                  onClick={() => openLibrary('element')}
                  className="border-[#e2e8f0] h-10 gap-1"
                  data-testid="add-image-element-btn"
                >
                  <ImagePlus className="w-3.5 h-3.5 text-[#94a3b8]" />
                  <span className="text-xs text-[#94a3b8]">Add Layer</span>
                </Button>
              </div>
            </div>
          )}

          {/* Tool-specific controls */}
          {activeTool === 'edit' && (
            <PhotoEditor
              filters={filters}
              onFiltersChange={setFilters}
              onRotate={handleRotate}
              onFlip={handleFlip}
            />
          )}

          {activeTool === 'text' && (
            <TextOverlay
              elements={elements}
              selectedId={selectedId}
              onAddText={addElement}
              onUpdateElement={updateElement}
              onDeleteElement={deleteElement}
            />
          )}

          {activeTool === 'templates' && (
            <TemplateGallery onSelectTemplate={handleSelectTemplate} />
          )}

          {activeTool === 'collage' && (
            <CollageBuilder
              layout={collageLayout}
              images={collageImages}
              onSelectLayout={handleCollageLayoutChange}
              onAddImage={handleCollageAddImage}
              onRemoveImage={handleCollageRemoveImage}
              canvasSize={canvasWidth}
              onOpenLibrary={(slotIndex) => openLibrary(`collage-${slotIndex}`)}
            />
          )}

          {activeTool === 'carousel' && (
            <CarouselBuilder
              slides={carouselSlides}
              activeSlide={activeSlide}
              onSetActiveSlide={handleSetActiveSlide}
              onAddSlide={handleAddSlide}
              onRemoveSlide={handleRemoveSlide}
              onUpdateSlide={handleUpdateSlide}
              onReorderSlide={handleReorderSlide}
              onOpenLibrary={() => openLibrary('carousel-slide')}
            />
          )}
        </div>

        {/* Center - Canvas Area */}
        <div className="flex-1 flex flex-col items-center justify-center bg-[#e2e8f0]/30 overflow-auto p-6">
          {/* Post context banner */}
          {postId && (
            <div className="flex items-center gap-3 mb-3 px-4 py-2 bg-[#8b5cf6]/10 border border-[#8b5cf6]/20 rounded-xl w-auto" data-testid="post-context-banner">
              <span className="text-xs font-semibold text-[#8b5cf6]">Editing image for post</span>
              <Button
                onClick={handleSaveToPost}
                disabled={isSavingToPost}
                className="bg-[#8b5cf6] hover:bg-[#7c3aed] text-white rounded-lg text-xs px-4 h-8"
                data-testid="save-to-post-btn"
              >
                <Save className="w-3.5 h-3.5 mr-1" />
                {isSavingToPost ? 'Saving...' : 'Save to Post'}
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => returnTo ? navigate(returnTo) : navigate('/dashboard')}
                className="text-xs text-[#8b5cf6]/60 hover:text-[#8b5cf6] h-8"
              >
                Cancel
              </Button>
            </div>
          )}

          {/* Top action bar */}
          <div className="flex items-center gap-2 mb-4">
            <Button variant="outline" size="sm" onClick={handleReset} className="border-[#e2e8f0] text-xs" data-testid="reset-btn">
              <RotateCcw className="w-3.5 h-3.5 mr-1" /> Reset
            </Button>
            {selectedId && (
              <Button variant="outline" size="sm" onClick={() => deleteElement(selectedId)} className="border-[#e2e8f0] text-xs text-red-500">
                <Trash2 className="w-3.5 h-3.5 mr-1" /> Delete
              </Button>
            )}
            {/* Crop button for selected image elements */}
            {selectedId && elements.find(e => e.id === selectedId && e.type === 'image') && (
              <Button
                variant="outline" size="sm"
                onClick={() => {
                  const el = elements.find(e => e.id === selectedId);
                  if (el?.src) openCropTool(el.src, selectedId);
                }}
                className="border-[#e2e8f0] text-xs text-amber-600"
                data-testid="crop-canvas-element-btn"
              >
                <Crop className="w-3.5 h-3.5 mr-1" /> Crop
              </Button>
            )}
            {/* Crop button for background image */}
            {!selectedId && backgroundImage && (
              <Button
                variant="outline" size="sm"
                onClick={() => openCropTool(backgroundImage, 'background')}
                className="border-[#e2e8f0] text-xs text-amber-600"
                data-testid="crop-background-btn"
              >
                <Crop className="w-3.5 h-3.5 mr-1" /> Crop Background
              </Button>
            )}
            <div className="flex-1" />
            <Button onClick={handleExport} className="btn-primary rounded-xl text-xs" data-testid="export-btn">
              <Download className="w-3.5 h-3.5 mr-1" /> Export PNG
            </Button>
            {postId && (
              <Button
                onClick={handleSaveToPost}
                disabled={isSavingToPost}
                className="bg-[#8b5cf6] hover:bg-[#7c3aed] text-white rounded-xl text-xs"
                data-testid="save-to-post-action-btn"
              >
                <Save className="w-3.5 h-3.5 mr-1" />
                {isSavingToPost ? 'Saving...' : 'Save to Post'}
              </Button>
            )}
            {activeTool === 'carousel' && carouselSlides.length > 1 && (
              <Button onClick={handleExportAllSlides} variant="outline" className="border-[#2563eb] text-[#2563eb] rounded-xl text-xs" data-testid="export-all-slides-btn">
                <Download className="w-3.5 h-3.5 mr-1" /> All Slides
              </Button>
            )}
          </div>

          {/* Canvas */}
          <div 
            className="inline-block rounded-lg overflow-hidden"
            style={{ 
              transform: `rotate(${rotation}deg)`, 
              transition: 'transform 0.3s',
              boxShadow: '0 0 0 1px rgba(0,0,0,0.08), 0 20px 40px rgba(0,0,0,0.12)'
            }}
          >
            <CanvasWorkspace
              width={canvasWidth}
              height={canvasHeight}
              elements={elements}
              selectedId={selectedId}
              onSelect={setSelectedId}
              onElementChange={updateElement}
              backgroundImage={backgroundImage}
              backgroundColor={backgroundColor}
              filters={filters}
              stageRef={stageRef}
              scale={scale}
            />
          </div>

          {/* Canvas info */}
          <p className="text-[10px] text-[#94a3b8] mt-3">
            {canvasWidth} x {canvasHeight}px
            {activeTool === 'carousel' && ` | Slide ${activeSlide + 1}/${carouselSlides.length}`}
          </p>
        </div>
      </div>

      {/* Image Library Modal */}
      <ImageLibrary
        isOpen={showLibrary}
        onClose={() => setShowLibrary(false)}
        onSelectImage={handleLibrarySelect}
      />

      {/* Image Crop Tool Modal */}
      <ImageCropTool
        isOpen={showCropTool}
        onClose={() => { setShowCropTool(false); setCropImageSrc(null); setCropTarget(null); }}
        imageSrc={cropImageSrc}
        onCropComplete={handleCropComplete}
      />
    </div>
  );
}
